// ============================================================
// Daftar SPK — list, filter, cetak & detail
// ============================================================

import axios from '../../config/axios.js';
import Alpine from 'alpinejs';

function createSpkIndex(config = {}) {
  const initialRows = config.rows || [];
  const initialMeta = config.meta || { current_page: 1, last_page: 1, total: 0, per_page: 20 };

  return {
    spkList: [...initialRows],
    loading: false,
    meta: { ...initialMeta },
    filters: {
      search: config.filters?.search || '',
      status: config.filters?.status || '',
      date: config.filters?.date || '',
      page: initialMeta.current_page || 1,
    },
    stats: [],
    showPayModal: false,
    payTarget: null,
    payMethod: 'cash',
    payAmount: 0,
    payLoading: false,
    _searchTimer: null,
    _ready: false,

    init() {
      this._ready = true;
      this.buildStats();
      this.$watch('filters.search', () => this.scheduleSearch());
    },

    hasActiveFilters() {
      return !!(this.filters.search.trim() || this.filters.status || this.filters.date);
    },

    scheduleSearch() {
      if (!this._ready) return;
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.fetchSpk();
      }, 300);
    },

    onFilterChange() {
      this.filters.page = 1;
      this.fetchSpk();
    },

    buildParams() {
      const params = new URLSearchParams();
      params.set('page', String(this.filters.page));
      params.set('per_page', '20');
      if (this.filters.search.trim()) params.set('search', this.filters.search.trim());
      if (this.filters.status) params.set('status', this.filters.status);
      if (this.filters.date) params.set('date', this.filters.date);
      return params;
    },

    async fetchSpk() {
      if (!this.hasActiveFilters()) {
        this.spkList = [...initialRows];
        this.meta = { ...initialMeta };
        this.filters.page = this.meta.current_page || 1;
        this.buildStats();
        return;
      }

      this.loading = true;
      try {
        const { data } = await axios.get(`/api/v1/kasir/spk?${this.buildParams()}`);
        this.spkList = data.data || [];
        this.meta = data.meta || this.meta;
        this.buildStats();
      } catch {
        window.toast?.('Gagal memuat daftar SPK.', 'error');
      } finally {
        this.loading = false;
      }
    },

    resetFilters() {
      this.filters = { search: '', status: '', date: '', page: 1 };
      this.spkList = [...initialRows];
      this.meta = { ...initialMeta };
      this.buildStats();
      window.history.replaceState({}, '', window.location.pathname);
    },

    buildStats() {
      const byStatus = (s) => this.spkList.filter((x) => x.status === s).length;
      this.stats = [
        { icon: '📋', label: 'Total SPK', value: this.meta.total || 0, colorClass: 'text-gray-800 dark:text-white' },
        { icon: '✅', label: 'Dikonfirmasi', value: byStatus('confirmed'), colorClass: 'text-blue-600' },
        { icon: '🔨', label: 'Produksi', value: this.spkList.filter((x) => ['desain', 'printing', 'finishing'].includes(x.status)).length, colorClass: 'text-amber-600' },
        { icon: '💰', label: 'Lunas', value: byStatus('paid'), colorClass: 'text-emerald-600' },
      ];
    },

    prevPage() {
      if (this.filters.page > 1) {
        this.filters.page--;
        this.fetchSpk();
      }
    },

    nextPage() {
      if (this.filters.page < this.meta.last_page) {
        this.filters.page++;
        this.fetchSpk();
      }
    },

    processPayment(spk) {
      this.payTarget = spk;
      this.payAmount = spk.total;
      this.payMethod = 'cash';
      this.showPayModal = true;
    },

    async submitPayment() {
      if (this.payMethod === 'cash' && this.payAmount < (this.payTarget?.total || 0)) {
        window.toast?.('Uang kurang dari total.', 'warning');
        return;
      }
      this.payLoading = true;
      try {
        await axios.post(`/api/v1/kasir/spk/${this.payTarget.id}/pay`, {
          payment_method: this.payMethod,
          amount_paid: this.payAmount,
        });
        window.toast?.('Pembayaran SPK berhasil!', 'success');
        this.showPayModal = false;
        this.fetchSpk();
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal proses pembayaran.', 'error');
      } finally {
        this.payLoading = false;
      }
    },

    statusLabel(s) {
      return ({
        draft: 'Draft',
        confirmed: 'Dikonfirmasi',
        desain: 'Desain',
        printing: 'Cetak',
        finishing: 'Finishing',
        ready: 'Siap Ambil',
        paid: 'Lunas',
        cancelled: 'Batal',
      })[s] || s;
    },

    statusBadge(s) {
      return ({
        draft: 'badge-gray',
        confirmed: 'badge-blue',
        desain: 'badge-yellow',
        printing: 'badge-yellow',
        finishing: 'badge-yellow',
        ready: 'badge-orange',
        paid: 'badge-green',
        cancelled: 'badge-red',
      })[s] || 'badge-gray';
    },

    isDueSoon(d) {
      if (!d) return false;
      const due = new Date(d.split('/').reverse().join('-'));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return due <= today;
    },
  };
}

Alpine.data('spkIndex', (config) => createSpkIndex(config));
window.spkIndex = createSpkIndex;
